import * as Dialog from '@radix-ui/react-dialog';
import { Calendar, Pencil, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { cn } from '@control-center/ui';

import { Field, Input } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';
import { useUpdateSubscription } from '../../hooks/use-subscriptions';
import { extractErrorMessage } from '../../lib/errors';

interface BillingDayFieldProps {
  subscriptionId: string;
  currentBillingDay: number | null;
  canEdit: boolean;
}

export function BillingDayField({
  subscriptionId,
  currentBillingDay,
  canEdit,
}: BillingDayFieldProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<string>(String(currentBillingDay ?? ''));
  const update = useUpdateSubscription();

  if (currentBillingDay === null) {
    return (
      <div className="flex items-center gap-2 text-[13px] text-muted">
        <Calendar size={13} strokeWidth={1.8} />
        Não aplicável (pagamento único)
      </div>
    );
  }

  const parsed = Number.parseInt(value, 10);
  const isValid = Number.isInteger(parsed) && parsed >= 1 && parsed <= 28;

  async function handleSubmit(): Promise<void> {
    if (!isValid) return;
    try {
      await update.mutateAsync({ id: subscriptionId, payload: { billing_day: parsed } });
      toast.success('Dia de cobrança atualizado. Vale a partir da próxima renovação.');
      setOpen(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos atualizar.'));
    }
  }

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-2 text-[13px] text-ink">
        <Calendar size={13} strokeWidth={1.8} className="text-muted" />
        Dia {currentBillingDay}
      </div>
      {canEdit && (
        <Dialog.Root
          open={open}
          onOpenChange={(o) => {
            setOpen(o);
            if (o) setValue(String(currentBillingDay));
          }}
        >
          <Dialog.Trigger asChild>
            <button
              type="button"
              aria-label="Editar dia de cobrança"
              title="Vale na próxima renovação"
              className="flex h-7 w-7 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-3 hover:text-ink"
            >
              <Pencil size={12} strokeWidth={1.8} />
            </button>
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay
              className={cn(
                'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              )}
            />
            <Dialog.Content
              className={cn(
                'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
                'w-[min(420px,calc(100vw-32px))] rounded-[14px] border border-line bg-panel shadow-lg p-6',
                'focus:outline-none',
              )}
            >
              <div className="mb-4 flex items-start justify-between gap-4">
                <Dialog.Title className="text-[18px] font-semibold text-ink">
                  Dia de cobrança
                </Dialog.Title>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    aria-label="Fechar"
                    className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted hover:bg-panel-2 hover:text-ink"
                  >
                    <X size={15} strokeWidth={2} />
                  </button>
                </Dialog.Close>
              </div>

              <p className="mb-4 text-[12px] text-muted">
                Vale na <strong>próxima renovação</strong> — não muda a cobrança que já está
                agendada. Valores aceitos: 1 a 28 (cap pra evitar fevereiro).
              </p>

              <Field
                label="Dia do mês"
                required
                error={value.length > 0 && !isValid ? 'Deve ser inteiro entre 1 e 28.' : undefined}
              >
                {({ inputId }) => (
                  <Input
                    id={inputId}
                    type="number"
                    min={1}
                    max={28}
                    step={1}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                  />
                )}
              </Field>

              <div className="mt-5 flex items-center justify-end gap-2 border-t border-line pt-4">
                <PillButton
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={update.isPending}
                >
                  Cancelar
                </PillButton>
                <PillButton
                  solid
                  type="button"
                  onClick={() => void handleSubmit()}
                  disabled={!isValid || update.isPending}
                >
                  {update.isPending ? 'Salvando…' : 'Salvar'}
                </PillButton>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </div>
  );
}
